import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useWebRTC } from '../hooks/useWebRTC';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhoneSlash, faMicrophone, faMicrophoneSlash, faVideo, faVideoSlash } from '@fortawesome/free-solid-svg-icons';

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT : Flèche SVG directionnelle
// ─────────────────────────────────────────────────────────────────────────────
function ArrowSVG({ x1, y1, x2, y2, color, strokeWidth = 2, preview = false }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 5) return null;

  const angle = Math.atan2(dy, dx);
  const headSize = Math.min(12, len * 0.3);
  
  // Raccourcir la ligne (la tête ne dépasse pas)
  const shorten = headSize * 0.3;
  const lineEndX = x2 - shorten * Math.cos(angle);
  const lineEndY = y2 - shorten * Math.sin(angle);
  
  // Points de la tête (angle fixe pour un rendu propre)
  const wingAngle = 0.6;
  const p1x = x2 - headSize * Math.cos(angle - wingAngle);
  const p1y = y2 - headSize * Math.sin(angle - wingAngle);
  const p2x = x2 - headSize * Math.cos(angle + wingAngle);
  const p2y = y2 - headSize * Math.sin(angle + wingAngle);

  return (
    <g opacity={preview ? 0.65 : 1}>
      <line
        x1={x1} y1={y1}
        x2={lineEndX} y2={lineEndY}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <polygon
        points={`${p1x},${p1y} ${x2},${y2} ${p2x},${p2y}`}
        fill={color}
        stroke={color}
        strokeWidth={strokeWidth * 0.5}
        strokeLinejoin="round"
      />
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT : Tracé libre SVG
// ─────────────────────────────────────────────────────────────────────────────
function FreehandPath({ points, color, strokeWidth = 2, preview = false }) {
  if (!points || points.length < 2) return null;

  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ');

  return (
    <path
      d={d}
      stroke={color}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={preview ? 0.65 : 1}
      filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.6))"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITAIRE : position relative en %
// ─────────────────────────────────────────────────────────────────────────────
function getRelativePercent(e, el) {
  const rect = el.getBoundingClientRect();

  const clientX = e.touches
    ? e.touches[0].clientX
    : e.clientX;

  const clientY = e.touches
    ? e.touches[0].clientY
    : e.clientY;

  return {
    x: ((clientX - rect.left) / rect.width) * 100,
    y: ((clientY - rect.top) / rect.height) * 100,
  };
}
// ─────────────────────────────────────────────────────────────────────────────
// EXPERT VIEW
// ─────────────────────────────────────────────────────────────────────────────
export default function ExpertView() {
  const { sessionId } = useParams();

  const {
    localStream,
    remoteStream,
    connectionState,
    isConnected,
    cameraError,
    localVideoRef,
    remoteVideoRef,
    socketRef,
  } = useWebRTC(sessionId, 'expert');

  const [activeTool, setActiveTool] = useState('arrow');
  const [color, setColor]= useState('#ff0000');
  const [annotationText, setAnnotationText] = useState('');
  const [isPlacing, setIsPlacing] = useState(false);
  const [arrowDraft, setArrowDraft] = useState(null);
  const [markerDraft, setMarkerDraft] = useState(null);
  const [strokeWidth, setStrokeWidth] = useState(1);

  //  État pour les contrôles audio/vidéo (expert)
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  useEffect(() => {
  if (localVideoRef.current && localStream) {
    localVideoRef.current.srcObject = localStream;
  }
}, [localStream]);

  const videoZoneRef = useRef(null);

  const annotations = useStore((state) => state.annotations);
  const addAnnotation = useStore((state) => state.addAnnotation);
  const removeAnnotation = useStore((state) => state.removeAnnotation);

  //  Couper/rétablir le micro (expert)
  const toggleMicrophone = () => {
    if (!localStream) return;
    
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) {
      const newState = !audioTrack.enabled;
      audioTrack.enabled = newState;
      setIsMuted(!newState);
    }
  };

  //  Couper/rétablir la caméra (expert)
  const toggleVideo = () => {
    if (!localStream) return;
    
    const videoTrack = localStream.getVideoTracks()[0];
    if (videoTrack) {
      const newState = !videoTrack.enabled;
      videoTrack.enabled = newState;
      setIsVideoOff(!newState);
    }
  };

  // SOCKETS ANNOTATIONS
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) {
      console.warn('Expert : socket non disponible');
      return;
    }
    console.log(' Expert : listeners annotations attachés');

    const handleNew = (annotation) => {
      console.log('EXPERT REÇOIT :', annotation.label || annotation.type, annotation);
      addAnnotation(annotation);
    };
    const handleDel = (id) => {
      console.log('🗑 EXPERT : suppression', id);
      removeAnnotation(id);
    };
    const handleSync = (annots) => {
      console.log(`EXPERT : sync de ${annots.length} annotations`);
      useStore.getState().clearAnnotations();
      annots.forEach((a) => addAnnotation(a));
    };

    socket.on('new-annotation', handleNew);
    socket.on('annotation-deleted', handleDel);
    socket.on('sync-annotations', handleSync);

    if (isConnected && sessionId) {
      setTimeout(() => socket.emit('request-sync-annotations', { sessionId }), 1000);
    }

    return () => {
      socket.off('new-annotation', handleNew);
      socket.off('annotation-deleted', handleDel);
      socket.off('sync-annotations', handleSync);
    };
  }, [socketRef, addAnnotation, removeAnnotation, isConnected, sessionId]);

  // ÉMISSION D'UNE ANNOTATION
  const emitAnnotation = useCallback((annotation) => {
    const socket = socketRef?.current;
    if (socket) {
      console.log(` EXPERT ENVOIE → Type: ${annotation.type}`, annotation);
      socket.emit('new-annotation', annotation);
    }
    addAnnotation(annotation);
    setIsPlacing(false);
    setAnnotationText('');
  }, [socketRef, addAnnotation]);

  // FLÈCHE
  const onArrowDown = useCallback((e) => {
    const el = videoZoneRef.current;
    if (!el) return;
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    const pos = getRelativePercent(e, el);
    setArrowDraft({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
  }, []);

  const onArrowMove = useCallback((e) => {
    if (!arrowDraft) return;
    const el = videoZoneRef.current;
    if (!el) return;
    const pos = getRelativePercent(e, el);
    setArrowDraft((prev) => ({ ...prev, x2: pos.x, y2: pos.y }));
  }, [arrowDraft]);

  const onArrowUp = useCallback((e) => {
    if (!arrowDraft) return;
    const el = videoZoneRef.current;
    if (!el) return;
    const pos = getRelativePercent(e, el);
    const final = { ...arrowDraft, x2: pos.x, y2: pos.y };

    const dx = final.x2 - final.x1;
    const dy = final.y2 - final.y1;
    if (Math.sqrt(dx * dx + dy * dy) < 2) {
      setArrowDraft(null);
      return;
    }

    emitAnnotation({
      id: crypto.randomUUID(),
      type: 'arrow',
      x: final.x1 / 100,
      y: final.y1 / 100,
      x2: final.x2 / 100,
      y2: final.y2 / 100,
      color,
      strokeWidth: strokeWidth,  
      label: '',
      createdBy: 'expert',
    });
    setArrowDraft(null);
  }, [arrowDraft, color, emitAnnotation]);

  // MARQUEUR
  const onMarkerDown = useCallback((e) => {
    const el = videoZoneRef.current;
    if (!el) return;
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    const pos = getRelativePercent(e, el);
    setMarkerDraft({ points: [pos] });
  }, []);

  const onMarkerMove = useCallback((e) => {
    if (!markerDraft) return;
    const el = videoZoneRef.current;
    if (!el) return;
    const pos = getRelativePercent(e, el);
    const last = markerDraft.points[markerDraft.points.length - 1];
    if ((pos.x - last.x) ** 2 + (pos.y - last.y) ** 2 < 0.3) return;
    setMarkerDraft((prev) => ({ points: [...prev.points, pos] }));
  }, [markerDraft]);

  const onMarkerUp = useCallback(() => {
    if (!markerDraft || markerDraft.points.length < 2) {
      setMarkerDraft(null);
      return;
    }
    emitAnnotation({
      id: crypto.randomUUID(),
      type: 'marker',
      x: markerDraft.points[0].x / 100,
      y: markerDraft.points[0].y / 100,
      points: markerDraft.points,
      color,
      strokeWidth: strokeWidth,  
      label: '',
      createdBy: 'expert',
    });
    setMarkerDraft(null);
  }, [markerDraft, color, emitAnnotation]);

  // TEXTE - CORRIGÉ
  const onTextClick = useCallback((e) => {
    const el = videoZoneRef.current;
    if (!el) {
      console.log(" videoZoneRef n'est pas disponible");
      return;
    }
    
    const rect = el.getBoundingClientRect();
    const clientX = e.clientX;
    const clientY = e.clientY;
    
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    
    console.log(" TEXTE CLICK - Position:", { x: (x * 100).toFixed(1) + '%', y: (y * 100).toFixed(1) + '%', texte: annotationText });
    
    emitAnnotation({
      id: crypto.randomUUID(),
      type: 'text',
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
      color,
      label: annotationText.trim() || 'Texte',
      createdBy: 'expert',
    });
  }, [videoZoneRef, color, annotationText, emitAnnotation]);

  // DISPATCHER
  const handlePointerDown = useCallback((e) => {
    if (!isPlacing) return;
    if (activeTool === 'arrow') onArrowDown(e);
    if (activeTool === 'marker') onMarkerDown(e);
    if (activeTool === 'text') onTextClick(e);
  }, [isPlacing, activeTool, onArrowDown, onMarkerDown, onTextClick]);

  const handlePointerMove = useCallback((e) => {
    if (activeTool === 'arrow') onArrowMove(e);
    if (activeTool === 'marker') onMarkerMove(e);
  }, [activeTool, onArrowMove, onMarkerMove]);

  const handlePointerUp = useCallback((e) => {
    if (activeTool === 'arrow') onArrowUp(e);
    if (activeTool === 'marker') onMarkerUp();
  }, [activeTool, onArrowUp, onMarkerUp]);

  // SUPPRESSION
  const handleDelete = useCallback((id) => {
    const socket = socketRef?.current;
    if (socket) {
      socket.emit('delete-annotation', id);
      console.log('🗑 EXPERT supprime :', id);
    }
    removeAnnotation(id);
  }, [socketRef, removeAnnotation]);

  // CURSEUR
  const getCursor = () => {
    if (!isPlacing) return 'default';
    if (activeTool === 'arrow' && arrowDraft) return 'grabbing';
    if (activeTool === 'marker' && markerDraft) return 'crosshair';
    return 'crosshair';
  };

  // RENDU OVERLAY ANNOTATIONS
  const renderAnnotationsOverlay = () => (
    <>
      {/* SVG pour flèches et marqueurs */}
      <svg
  style={{
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 15,
  }}
  viewBox="0 0 100 100"
  preserveAspectRatio="none"
>
        {annotations.map((ann) => {
          if (ann.type === 'arrow' && ann.x2 !== undefined) {
            return (
              <ArrowSVG
                key={ann.id}
                x1={ann.x * 100}
                y1={ann.y * 100}
                x2={ann.x2 * 100}
                y2={ann.y2 * 100}
                color={ann.color || '#ff0000'}
                strokeWidth={ann.strokeWidth || 2}
              />
            );
          }
          if (ann.type === 'marker' && ann.points) {
            return (
              <FreehandPath
                key={ann.id}
                points={ann.points}
                color={ann.color || '#ffff00'}
                strokeWidth={ann.strokeWidth || 2}
              />
            );
          }
          return null;
        })}

        {arrowDraft && (
          <ArrowSVG
            x1={arrowDraft.x1}
            y1={arrowDraft.y1}
            x2={arrowDraft.x2}
            y2={arrowDraft.y2}
            color={color}
            preview
          />
        )}

        {markerDraft && (
          <FreehandPath
            points={markerDraft.points}
            color={color}
            preview
          />
        )}
      </svg>

      {/* TEXTE en dehors du SVG */}
      {annotations.map((ann) => {
        if (ann.type !== 'text') return null;
        
        console.log(" RENDU TEXTE:", { x: ann.x * 100 + '%', y: ann.y * 100 + '%', label: ann.label });
        
        return (
          <div
            key={ann.id}
            style={{
              position: 'absolute',
              left: `${ann.x * 100}%`,
              top: `${ann.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              background: ann.color || '#e94560',
              color: 'white',
              padding: '6px 15px',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              zIndex: 50,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
              fontFamily: 'Arial, sans-serif',
            }}
          >
            {ann.label || 'Texte'}
          </div>
        );
      })}
    </>
  );

  // ERREUR CAMERA
  if (cameraError) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', color: 'white' }}>
        <h1 style={{ color: '#e94560' }}>Erreur Camera</h1>
        <p style={{ fontFamily: 'monospace', background: '#111', padding: '10px', borderRadius: '6px' }}>{cameraError}</p>
        {cameraError.includes('getUserMedia') && (
          <p style={{ color: '#aaa', fontSize: '14px', maxWidth: '400px', textAlign: 'center' }}>
            Acces camera necessite HTTPS ou localhost.
          </p>
        )}
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '12px 24px', marginTop: '20px', background: '#e94560', border: 'none', color: 'white', borderRadius: '8px', cursor: 'pointer' }}
        >
          Reessayer
        </button>
      </div>
    );
  }

  // RENDU PRINCIPAL
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a192f', color: 'white' }}>

      {/* SIDEBAR */}
      <div style={{ width: '300px', background: '#1a365d', padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div>
          <h2 style={{ margin: '0 0 4px 0', color: '#e94560' }}>SOS EXPERT</h2>
          <h3 style={{ margin: 0 }}>Console Expert</h3>
          <p style={{ fontSize: '13px', opacity: 0.6, marginTop: '4px' }}>Session : {sessionId}</p>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.07)', padding: '12px', borderRadius: '10px', fontSize: '13px', lineHeight: '1.8' }}>
          <div>{isConnected ? 'Operateur connecte' : 'En attente...'}</div>
          <div>WebRTC : <strong style={{ color: connectionState === 'connected' ? '#4CAF50' : connectionState === 'connecting' ? '#FFC107' : '#aaa' }}>{connectionState}</strong></div>
          <div>Ma camera : {localStream ? 'Active' : 'Demarrage...'}</div>
          <div>Flux operateur : {remoteStream ? 'Recu' : 'En attente'}</div>
        </div>

        <div>
          <h4 style={{ margin: '0 0 6px 0' }}>Outils</h4>

          {isPlacing && (
            <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 8px 0' }}>
              {activeTool === 'arrow' && ' Cliquez-glissez pour orienter la flèche'}
              {activeTool === 'marker' && ' Maintenez et dessinez librement'}
              {activeTool === 'text' && ' Cliquez pour placer le texte'}
            </p>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'arrow', label: ' Flèche' },
              { id: 'marker', label: ' Marqueur' },
              { id: 'text', label: ' Texte' },
            ].map((tool) => (
              <button
                key={tool.id}
                onClick={() => { setActiveTool(tool.id); setIsPlacing(true); }}
                style={{
                  padding: '8px 14px', fontSize: '13px',
                  background: activeTool === tool.id && isPlacing ? '#e94560' : 'rgba(255,255,255,0.1)',
                  color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {tool.label}
              </button>
            ))}
          </div>

          {activeTool === 'text' && isPlacing && (
            <input
              type="text"
              placeholder="Texte annotation..."
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              style={{
                marginTop: '10px', width: '100%', padding: '8px',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px', color: 'white', fontSize: '13px', boxSizing: 'border-box',
              }}
            />
          )}

          {isPlacing && (
            <button
              onClick={() => { setIsPlacing(false); setArrowDraft(null); setMarkerDraft(null); }}
              style={{ marginTop: '8px', width: '100%', padding: '6px', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#aaa', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
            >
              Annuler
            </button>
          )}
        </div>

        <div>
          <h4 style={{ margin: '0 0 10px 0' }}>Couleur</h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            {['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#090909'].map((c) => (
              <button
                key={c}
                tabIndex="-1" 
                onClick={() => setColor(c)}
                style={{
                  width: '28px', height: '28px', background: c,
                  border: color === c ? '3px solid white' : '2px solid transparent',
                  borderRadius: '50%', cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
        {/* Épaisseur du trait */}
        <div>
          <h4 style={{ margin: '0 0 10px 0' }}>Épaisseur</h4>
          <input
            type="range"
            min="0.5"
            max="5"
            step="0.5"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(parseFloat(e.target.value))}
            style={{ width: '100%' }}
          />
          <div style={{ fontSize: '12px', textAlign: 'center', marginTop: '5px' }}>
            {strokeWidth} px
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <h4 style={{ margin: '0 0 10px 0' }}>Annotations ({annotations.length})</h4>
          {annotations.length === 0 && (
            <p style={{ fontSize: '12px', color: '#555' }}>Aucune annotation pour cette session.</p>
          )}
          {annotations.map((ann) => (
            <div
              key={ann.id}
              style={{
                background: 'rgba(255,255,255,0.05)', padding: '8px 10px',
                borderRadius: '8px', marginBottom: '6px', fontSize: '12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <span>
                <span style={{ color: ann.color, marginRight: '6px' }}>●</span>
                {ann.type === 'marker' ? ' tracé' : ann.label || ann.type}
                <span style={{ color: '#555', marginLeft: '6px' }}>
                  ({Math.round(ann.x * 100)}%, {Math.round(ann.y * 100)}%)
                </span>
              </span>
              <button
                onClick={() => handleDelete(ann.id)}
                style={{ background: '#e94560', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' }}
              >
                X
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ZONE VIDÉO */}
      <div style={{ flex: 1, position: 'relative', padding: '20px' }}>

        <div
          ref={videoZoneRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{
            width: '100%', height: '75%', background: '#000',
             borderRadius: '15px', overflow: 'hidden', 
             position: 'relative',
            cursor: getCursor(),
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          {remoteStream ? (
            <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#444' }}>
              <p style={{ fontSize: '48px', margin: 0 }}></p>
              <p>En attente de l'operateur...</p>
              <p style={{ fontSize: '13px' }}>ID : <strong>{sessionId}</strong></p>
            </div>
          )}

          {renderAnnotationsOverlay()}

          {isPlacing && (
            <div style={{
              position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(233,69,96,0.92)', color: 'white',
              padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', fontSize: '14px',
              pointerEvents: 'none', zIndex: 30,
            }}>
              {activeTool === 'arrow' && ' Cliquez et glissez pour orienter'}
              {activeTool === 'marker' && ' Maintenez et dessinez'}
              {activeTool === 'text' && ' Cliquez pour placer'}
            </div>
          )}
        </div>
      
        {/* NOUVEAU: Bouton couper micro (expert) */}
        <button
          onClick={toggleMicrophone}
          style={{
            position: 'absolute',
            bottom: 90,
            left: 30,
            zIndex: 100,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            background: isMuted ? 'rgba(220, 53, 69, 0.8)' : 'rgba(0,0,0,0.55)',
            color: 'white',
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} size="lg" />
        </button>

        {/* NOUVEAU: Bouton couper caméra (expert) */}
        <button
          onClick={toggleVideo}
          style={{
            position: 'absolute',
            bottom: 90,
            left: 100,
            zIndex: 100,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            background: isVideoOff ? 'rgba(220, 53, 69, 0.8)' : 'rgba(0,0,0,0.55)',
            color: 'white',
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FontAwesomeIcon icon={isVideoOff ? faVideoSlash : faVideo} size="lg" />
        </button>

        {/* Bouton quitter la session (en bas à gauche de la vidéo) */}
        <button
          onClick={() => {
            if (window.confirm('Voulez-vous vraiment quitter la session ?')) {

              const socket = socketRef?.current;

              if (socket) {
                socket.emit('end-call', { sessionId });
              }

              if (localStream) {
                localStream.getTracks().forEach(track => track.stop());
              }

              if (remoteStream) {
                remoteStream.getTracks().forEach(track => track.stop());
              }

              window.location.href = '/';
            }
          }}
          style={{
            position: 'absolute',
            bottom: 90,
            left: 170,
            zIndex: 100,
            padding: '14px 20px',
            background: '#e94560',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span><FontAwesomeIcon icon={faPhoneSlash} color="white" size="sm" /></span> Couper
        </button>

        {/* CAMÉRA LOCALE */}
        <div style={{
          position: 'absolute', bottom: '30px', right: '30px',
          width: '220px', height: '165px', background: '#000',
          borderRadius: '12px', overflow: 'hidden',
          border: '3px solid #4CAF50',
        }}>
            <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: 'scaleX(-1)',
              display: localStream ? 'block' : 'none',
            }}
          />
          {!localStream && (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '12px' }}>
              Camera...
            </div>
          )}
          <div style={{
            position: 'absolute', bottom: '6px', left: '8px',
            background: 'rgba(76,175,80,0.9)', padding: '3px 10px',
            borderRadius: '4px', fontSize: '11px', fontWeight: 'bold',
          }}>
            Expert (vous)
          </div>
        </div>

      </div>
    </div>
  );
}